/**
 * Pure utility: groups checklist contacts and quote requests by service slug,
 * counts unique emails per stage, computes converted rows (same email in both
 * stages) and returns per-row + totals. No React, no side-effects.
 */
const norm = (e) => (e || "").trim().toLowerCase();

function indexEmailsBySlug(items, slugOf) {
  const out = {};
  items.forEach((item) => {
    const slug = slugOf(item);
    if (!slug) return;
    if (!out[slug]) out[slug] = new Set();
    out[slug].add(norm(item.email));
  });
  return out;
}

function buildRow(slug, checklistEmails, quoteEmails) {
  const downloads = checklistEmails?.size || 0;
  const quotesCount = quoteEmails?.size || 0;
  const converted = checklistEmails
    ? [...checklistEmails].filter((e) => quoteEmails?.has(e))
    : [];
  const rate = downloads > 0 ? Math.round((converted.length / downloads) * 100) : 0;
  return { slug, downloads, quotes: quotesCount, converted: converted.length, rate };
}

function computeTotals(rows) {
  const totals = rows.reduce(
    (acc, r) => ({
      downloads: acc.downloads + r.downloads,
      quotes: acc.quotes + r.quotes,
      converted: acc.converted + r.converted,
    }),
    { downloads: 0, quotes: 0, converted: 0 }
  );
  totals.rate =
    totals.downloads > 0 ? Math.round((totals.converted / totals.downloads) * 100) : 0;
  return totals;
}

/**
 * computeFunnel — given raw contacts + quotes, returns `{ rows, totals }`
 * sorted by combined activity.
 */
export function computeFunnel(contacts, quotes) {
  const checklistBySlug = indexEmailsBySlug(contacts, (c) =>
    (c.source || "").startsWith("checklist-")
      ? c.source.replace(/^checklist-/, "")
      : null
  );
  const quotesBySlug = indexEmailsBySlug(quotes, (q) => q.service_slug);

  const slugs = new Set([
    ...Object.keys(checklistBySlug),
    ...Object.keys(quotesBySlug),
  ]);
  const rows = Array.from(slugs)
    .map((slug) => buildRow(slug, checklistBySlug[slug], quotesBySlug[slug]))
    .sort((a, b) => b.downloads + b.quotes - (a.downloads + a.quotes));

  return { rows, totals: computeTotals(rows) };
}
